import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
// components
import { Menu, Modal } from 'antd';
// actions
// drug and drop
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
// styles
import './folders.css';
import { Link } from 'react-router-dom';
// map publisher
import { publish } from 'app/libs/core/PubSub';
import * as way from 'app/libs/core/PubSubWays';
import {
  applyingTypes,
  applyingPublic,
  applyingPrivate,
} from 'app/constants/folder.constants';
import withTranslations from 'app/components/HOGs/withTranslations';
import pageCache from 'app/API/cache.service';
import {
  fetchFolders,
  createFolder,
  fetchTours,
} from '../../../../actions/Menu/folders-async.actions';
import * as types from '../../../../actions/action_types';
import FolderInput, { warnings } from './FolderInput';
import TourFolder from './TourFolder';
import { setMenuFolderCreateVisible } from 'actions/Menu/main_menu.action';

function countDefaultFolders(folderList) {
  return folderList
    .map(({ title }) => title.match(/^Folder \d+$/))
    .filter((title) => !!title).length;
}

function zoomToTours(tours) {
  if (tours.length === 1) {
    const { lat, lng } = tours[0];
    const bounds = {
      latLng: [lat, lng],
      zoom: 11,
    };
    publish(way.DRIVE_BUILDER_MOVE_MAP, bounds);
  } else if (tours.length > 1) {
    const { max, min } = Math;
    const allLats = tours.map(({ lat }) => lat);
    const allLngs = tours.map(({ lng }) => lng);
    const bounds = [
      [min(...allLats), min(...allLngs)],
      [max(...allLats), max(...allLngs)],
    ];
    publish(way.DRIVE_BUILDER_MAP_FIT_BOUNDS, bounds);
  }
}

export function getFolderListCollapse(applying, isMobile, folderList) {
  const folderNameList = folderList.map(({ title }) => title);

  const items = [];

  const getType = () => {
    switch (applying) {
      case applyingTypes.PLACE_FOLDER:
        return 'place';
      case applyingTypes.DENMARK_FOLDER:
        return 'denmark';
      case applyingTypes.FRANCE_FOLDER:
        return 'france';
      case applyingTypes.GERMANY_FOLDER:
        return 'germany';
      default:
        return 'tour';
    }
  };

  const renderFolder = ({ title, id, tours }) => {
    return (
      <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
        <TourFolder
          id={id}
          index={id}
          name={title}
          tourCounter={tours.length}
          folderNameList={folderNameList.filter(
            (folderName) => folderName !== title
          )}
          isMobile={isMobile}
          setDraggingId={() => false}
          applying={applying}
        />
      </DndProvider>
    );
  };

  folderList.forEach((folder) => {
    items.push({
      label: (
        <Link
          to={`/${getType()}/folders/${encodeURI(
            folder.title.split(' ').join('-')
          )}_${folder.id}/list`}
          draggable={false}
        >
          {renderFolder(folder)}
        </Link>
      ),
      className: 'collapsed-folder-items',
      key: `${folder.id}${applying}`,
    });
  });

  return items;
}

let prevId;
function FolderList({
  authUser,
  isAddFolder,
  hideAddFolderMenu,
  active,
  collapsed,
  isMobile,
  applying,
  tr,
}) {
  const dispatch = useDispatch();
  const folderListTour = useSelector((state) => state.folders);
  const folderListPlace = useSelector((state) => state.foldersPlace);
  const folderListDenmark = pageCache.get('dreamFolders')?.Denmark || [];
  const folderListFrance = pageCache.get('dreamFolders')?.France || [];
  const folderListGermany = pageCache.get('dreamFolders')?.Germany || [];

  const getList = () => {
    switch (applying) {
      case applyingTypes.TOUR_FOLDER:
        return folderListTour;
      case applyingTypes.PLACE_FOLDER:
        return folderListPlace;
      case applyingTypes.DENMARK_FOLDER:
        return folderListDenmark;
      case applyingTypes.FRANCE_FOLDER:
        return folderListFrance;
      case applyingTypes.GERMANY_FOLDER:
        return folderListGermany;
      default:
        return [];
    }
  };

  const folderList = getList();

  const pathname = useSelector((state) => state.router.location.pathname);
  const defaultOpen = applyingPrivate(applying)
    ? pathname?.split('/')[3]?.split('_').pop()
    : pathname?.split('/')[4]?.split('_').pop();
  const [draggingId, setDraggingId] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState(
    defaultOpen ? [defaultOpen] : []
  );
  const [newFolderName, setNewFolderName] = useState('Folder 1');
  const [warning, changeWarning] = useState(warnings.empty);

  const folderNameList = folderList.map(({ title }) => title);
  const userId = authUser?.id || 0;
  const defaultFolderName = useMemo(
    () => `Folder ${countDefaultFolders(folderList) + 1}`,
    [folderList]
  );

  useEffect(() => {
    if (userId !== prevId) {
      prevId = userId;
      dispatch(fetchFolders(userId));
    }
  });

  useEffect(() => {
    if (!active) setSelectedFolder([]);
  }, [active]);

  useEffect(() => {
    setNewFolderName(defaultFolderName);
  }, [folderList]);

  const finishCreateFolder = (e) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      if (e.key === 'Enter' && !warning.show && newFolderName) {
        const folder = {
          title: newFolderName,
          orderId: folderList.length,
          applying,
        };
        dispatch(createFolder(userId, folder));
      } else if (e.key === 'Escape') {
        setNewFolderName(defaultFolderName);
      }
      changeWarning(warnings.empty);
      dispatch(setMenuFolderCreateVisible(false));
      hideAddFolderMenu();
    }
  };

  const changeFolderName = (value) => {
    setNewFolderName(value);
    if (value.length > 255) {
      changeWarning(warnings.maxLength);
    } else if (folderNameList.includes(value)) {
      changeWarning(warnings.wrongName);
    } else {
      changeWarning(warnings.empty);
    }
  };

  const chooseFolder = (e) => {
    if (applyingPublic(applying)) {
      const folderId = e.key;
      dispatch({ type: types.FETCH_PREVIEW_DATA_CLEANE });
      dispatch({ type: types.FETCH_PREVIEW_POLYLINE_CLEANE });
      setSelectedFolder([folderId]);
    } else if (e.domEvent.target.tagName !== 'svg') {
      const folderId = e.key;
      dispatch({ type: types.FETCH_PREVIEW_DATA_CLEANE });
      dispatch({ type: types.FETCH_PREVIEW_POLYLINE_CLEANE });
      setSelectedFolder([folderId]);
      dispatch(fetchTours(folderId, userId, applying)).then((tours = []) =>
        zoomToTours(tours)
      );
    }
  };

  const renderFolder = ({ title, id, tours }) => {
    return (
      <TourFolder
        id={id}
        index={id}
        name={title}
        tourCounter={tours.length}
        folderNameList={folderNameList.filter(
          (folderName) => folderName !== title
        )}
        isMobile={isMobile}
        setDraggingId={setDraggingId}
        applying={applying}
      />
    );
  };

  const renderItems = () => {
    const items = [];

    if (isAddFolder && !isMobile) {
      items.push({
        label: (
          <FolderInput
            value={newFolderName}
            onChange={changeFolderName}
            onKeyDown={finishCreateFolder}
            warning={warning}
            isMobile={false}
          />
        ),
        disabled: true,
        className: 'new-folder-item',
      });
    }

    const getType = () => {
      switch (applying) {
        case applyingTypes.PLACE_FOLDER:
          return 'place';
        case applyingTypes.DENMARK_FOLDER:
          return 'dreamtours/denmark';
        case applyingTypes.FRANCE_FOLDER:
          return 'dreamtours/france';
        case applyingTypes.GERMANY_FOLDER:
          return 'dreamtours/germany';
        default:
          return 'tour';
      }
    };

    folderList.forEach((folder) => {
      items.push({
        label: (
          <Link
            to={`/${getType()}/folders/${encodeURI(
              folder.title.replace('/', ' ').split(' ').join('-')
            )}_${folder.id}/list`}
            draggable={false}
          >
            {renderFolder(folder)}
          </Link>
        ),
        key: folder.id,
        style: { opacity: draggingId === folder.id ? 0 : 1 },
      });
    });

    return items;
  };

  return (
    <>
      <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
        <Menu
          mode="inline"
          theme="light"
          className="folders-menu"
          onSelect={(e) => chooseFolder(e)}
          selectedKeys={selectedFolder}
          inlineIndent={collapsed ? 0 : 40}
          draggable={false}
          selectable={!draggingId}
          items={renderItems()}
        />
      </DndProvider>
      <Modal
        title={tr['Input folder name']}
        visible={isAddFolder && isMobile}
        okText={tr.Save}
        cancelText={tr.Cancel}
        onOk={() => finishCreateFolder({ key: 'Enter' })}
        onCancel={() => finishCreateFolder({ key: 'Escape' })}
        bodyStyle={{ paddingTop: '10px' }}
        className="folder-title-modal"
      >
        <FolderInput
          value={newFolderName}
          onChange={changeFolderName}
          onKeyDown={() => {}}
          warning={warning}
          isMobile
        />
      </Modal>
    </>
  );
}

FolderList.propTypes = {
  authUser: PropTypes.object,
  isAddFolder: PropTypes.bool,
  hideAddFolderMenu: PropTypes.func.isRequired,
  active: PropTypes.bool,
};

export default withTranslations(FolderList);
